import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Progress } from '@/components/ui/progress';
import { ToastAction } from '@/components/ui/toast';

export default function DocumentUpload() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PDF or image file (JPG, PNG)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload a file smaller than 10MB',
        variant: 'destructive',
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a file to upload',
        variant: 'destructive',
      });
      return;
    }

    if (!documentType) {
      toast({
        title: 'Document type required',
        description: 'Please select a document type',
        variant: 'destructive',
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to upload documents',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Get client record
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (clientError) throw clientError;

      if (!clientData) {
        toast({
          title: 'Profile incomplete',
          description: 'Please complete your profile to upload documents',
          variant: 'destructive',
          action: (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/client-portal/profile'}
            >
              Complete Profile
            </Button>
          ),
        });
        setIsUploading(false);
        return;
      }

      const clientId = clientData.id;

      // Generate unique filename
      const timestamp = Date.now();
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${timestamp}-${selectedFile.name}`;
      const filePath = `${clientId}/${fileName}`;

      setUploadProgress(30);

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('tax-documents')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      setUploadProgress(60);

      // Insert metadata into client_documents table
      const { data: insertedDoc, error: insertError } = await supabase
        .from('client_documents')
        .insert({
          client_id: clientId,
          file_name: selectedFile.name,
          file_path: filePath,
          file_type: documentType,
          file_size: selectedFile.size,
          mime_type: selectedFile.type,
          tax_year: parseInt(taxYear),
          extraction_status: 'pending',
          requires_admin_approval: true,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setUploadProgress(80);

      // Trigger AI processing edge function
      try {
        await supabase.functions.invoke('process-tax-document', {
          body: { documentId: insertedDoc.id },
        });
      } catch (aiError) {
        console.error('AI processing error (non-blocking):', aiError);
        // Don't fail the upload if AI processing fails
      }

      setUploadProgress(100);

      toast({
        title: 'Document uploaded successfully!',
        description: 'Your document is being processed and will be reviewed by our team.',
      });

      // Reset form
      setSelectedFile(null);
      setDocumentType('');
      setTaxYear(new Date().getFullYear().toString());
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'There was an error uploading your document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload Tax Documents</CardTitle>
        <CardDescription>
          Upload your W-2s, 1099s, receipts, and other tax documents securely
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Document Type Selector */}
        <div className="space-y-2">
          <Label htmlFor="documentType">Document Type *</Label>
          <Select value={documentType} onValueChange={setDocumentType}>
            <SelectTrigger id="documentType">
              <SelectValue placeholder="Select document type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="w2">W-2 (Wage and Tax Statement)</SelectItem>
              <SelectItem value="1099-misc">1099-MISC</SelectItem>
              <SelectItem value="1099-int">1099-INT (Interest Income)</SelectItem>
              <SelectItem value="1099-nec">1099-NEC (Non-Employee Compensation)</SelectItem>
              <SelectItem value="receipt">Receipt</SelectItem>
              <SelectItem value="social-security-card">Social Security Card</SelectItem>
              <SelectItem value="drivers-license">Drivers License</SelectItem>
              <SelectItem value="other">Other Tax Document</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tax Year Selector */}
        <div className="space-y-2">
          <Label htmlFor="taxYear">Tax Year *</Label>
          <Select value={taxYear} onValueChange={setTaxYear}>
            <SelectTrigger id="taxYear">
              <SelectValue placeholder="Select tax year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Drag and Drop Zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-primary/50'
          }`}
        >
          {selectedFile ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFile}
                  className="ml-auto"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">
                Drag and drop your file here
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                or click to browse
              </p>
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileInputChange}
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                Choose File
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Supported formats: PDF, JPG, PNG (Max 10MB)
              </p>
            </>
          )}
        </div>

        {/* Upload Button */}
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || !documentType || isUploading}
          className="w-full"
        >
          {isUploading ? 'Uploading...' : 'Upload Document'}
        </Button>

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <Progress value={uploadProgress} />
            <p className="text-xs text-center text-muted-foreground">
              {uploadProgress}% complete
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
